//-----------------------------------------------------------------------------
// Based on https://github.com/tronicboy1/lit-markdown
//-----------------------------------------------------------------------------

import { Directive, directive } from "lit/directive.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import sanitizeHTML from "sanitize-html";

type Options = typeof MarkdownDirective.defaultOptions;

/**
 * An async directive to render markdown in a LitElement's render function.
 * Images can be included or removed in the executor's options.
 */
export class MarkdownDirective extends Directive {
    static defaultOptions = {
        includeImages: false,
        includeCodeBlockClassNames: false,
        skipSanitization: false,
        loadingHTML: "<p>Loading...</p>",
        openLinksInNewTab: true,
    };

    private sanitizeHTMLWithOptions(rawHTML: string, options: Options): string {
        const allowedTags = options.includeImages
            ? [...sanitizeHTML.defaults.allowedTags, "img"]
            : sanitizeHTML.defaults.allowedTags;
        const allowedClasses: sanitizeHTML.IOptions["allowedClasses"] =
            options.includeCodeBlockClassNames ? { code: ["*"] } : {};
        return sanitizeHTML(rawHTML, { allowedTags, allowedClasses });
    }

    render(rawMarkdown: string, options?: Partial<Options>) {
        const mergedOptions = Object.assign(MarkdownDirective.defaultOptions, options ?? {});
        const renderer = new marked.Renderer();
        if (mergedOptions.openLinksInNewTab) {
            renderer.link = function (
                href: string,
                title: string | null | undefined,
                text: string
            ) {
                const link = marked.Renderer.prototype.link.apply(this, [href, title, text]);
                return link.replace("<a", "<a target='_blank'");
            };
        }
        const markedOptions = {
            renderer: renderer,
        };

        let content = marked.parse(rawMarkdown, markedOptions);
        if (!mergedOptions.skipSanitization) {
            content = this.sanitizeHTMLWithOptions(content, mergedOptions);
        }
        return unsafeHTML(content);
    }
}

/**
 * An directive used to render markdown in a LitElement's render function.
 *
 * Rendering pictures can be enabled through the settings.
 * Css class names for code blocks may also be enabled through settings.
 *
 * setting the "skipSanitization" option to true will skip the sanitization process and render markdown as raw HTML.
 * _Use with caution!_
 *
 * The default loading html may also be replaced.
 * This default HTML is also sanitized by default.
 * If the "skipSanitization" option is true, the default HTML will also not be sanitized.
 *
 * @example render() {
 *            const rawMarkdown = `# Hello World`
 *            return html`<article>${resolveMarkdown(rawMarkdown)}</article>`
 * }
 *
 * @example render() {
 *            const rawMarkdown = `# Hello World
 *            ![image.jpeg](https://host.com/image.jpeg "image.jpeg")`;
 *            return html`<article>${resolveMarkdown(rawMarkdown, { includeImages: true, includeCodeBlockClassNames: true, loadingHTML: "<loading-icon></loading-icon>" })}</article>`
 * }
 * @typedef {Parameters<InstanceType<typeof MarkdownDirective>['render']>} RenderParameters
 * @param {RenderParameters[0]} rawMarkdown Markdown to be rendered.
 * @param {RenderParameters[1]} options
 */
export const resolveMarkdown = directive(MarkdownDirective);
